// central Axios instance with Auth header
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5001/api";
export const api = axios.create({ baseURL: API_URL });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      // Only force logout if user was previously logged in (had a token)
      // Don't trigger logout for unauthenticated pages like Pricing
      const hadToken = localStorage.getItem("token");
      if (hadToken) {
        localStorage.removeItem("token");
        localStorage.removeItem("username");
        window.dispatchEvent(new Event("auth:logout"));
      }
    }
    return Promise.reject(error);
  }
);

// Auth endpoints
export const login = (username, password) => api.post("/login", { username, password });
export const register = (username, password, email) => api.post("/register", { username, password, email });
export const forgotPassword = (identifier) => api.post("/auth/forgot-password", { email: identifier });
export const resetPassword = (token, new_password) => api.post("/auth/reset-password", { token, new_password });
export const getMe = () => api.get("/me");
export const updateMe = (data) => api.put("/me", data);

// Social endpoints
export const getSocialAccounts = () => api.get("/social/accounts");
export const connectSocialAccount = (platform, handle) =>
  api.post("/social/connect", { platform, handle });
export const generateContent = (topic) =>
  api.post("/social/generate-content", { topic });
export const generateImage = (prompt, options = {}) =>
  api.post("/social/generate-image", { prompt, ...options }, { timeout: 120000 });
export const generateVideo = (prompt, options = {}) =>
  api.post("/social/generate-video", { prompt, ...options }, { timeout: 300000 });
export const schedulePost = (payload) => api.post("/social/schedule", payload);
export const postNow = (payload) => api.post("/social/post-now", payload);
export const postAll = (payload) => api.post("/social/post-all", payload);
export const repurposeContent = (content, platforms) => api.post("/social/repurpose", { content, platforms });
export const getOptimalTimes = () => api.get("/social/optimal-times");
export const getScheduledPosts = () => api.get("/social/scheduled-posts");
export const deleteScheduledPost = (postId) => api.delete(`/social/scheduled-posts/${postId}`);
export const getAnalytics = () => api.get("/analytics");
export const getPostPerformance = () => api.get("/social/post-performance");
export const getTwitterAuthUrl = () => api.get("/social/twitter/auth-url");
export const getTikTokAuthUrl = () => api.get("/social/tiktok/login");
export const getPinterestAuthUrl = () => api.get("/social/pinterest/login");
export const getLinkedInAuthUrl = () => api.get("/social/linkedin/auth-url");
export const getTeam = () => api.get("/teams");
export const inviteToTeam = (username) => api.post("/teams/invite", { username });
export const getSharedContent = () => api.get("/shared-content");
export const createSharedContent = (data) => api.post("/shared-content", data);
export const getApprovals = () => api.get("/approvals");
export const approveContent = (approvalId) => api.post(`/approvals/${approvalId}/approve`);
export const abTestPosts = (data) => api.post("/ai/ab-test", data);
export const getCalendarSuggestions = () => api.get("/ai/calendar-suggestions");
export const getSubscription = () => api.get("/subscription");
export const upgradeSubscription = (tier) => api.post("/subscription/upgrade", { tier });

// Proposals endpoints
export const getPlatforms = () => api.get("/proposals/platforms");
export const connectPlatform = (name, url) =>
  api.post("/proposals/connect-platform", { name, profile_url: url });
export const findJobs = (query, platform = "all") =>
  api.get("/proposals/find-jobs", {
    params: { query, platform, limit: 25 },
    timeout: 90000,
  });
export const generateProposal = (job_title, job_description = "", options = {}) =>
  api.post("/proposals/generate", {
    job_title,
    job_description,
    tone: options.tone,
    length: options.length,
    focus_areas: options.focus,
    custom_notes: options.customNotes,
    job_budget: options.budget,
    platform: options.platform,
    profile_id: options.profile_id,
  });

export const markProposal = (proposal_id, status) =>
  api.post("/proposals/mark", { proposal_id, status });
export const getProposalStats = () => api.get("/proposals/stats");

// Freelancer Profile endpoints
export const getFreelancerProfile = () => api.get("/profile/freelancer");
export const listFreelancerProfiles = () => api.get("/profile/freelancer/all");
export const getFreelancerProfileById = (id) => api.get(`/profile/freelancer/${id}`);
export const createFreelancerProfile = (data) => api.post("/profile/freelancer", data);
export const updateFreelancerProfile = (id, data) => api.put(`/profile/freelancer/${id}`, data);
export const saveFreelancerProfile = (profileData) => api.put("/profile/freelancer", profileData);
export const activateFreelancerProfile = (id) => api.post(`/profile/freelancer/${id}/activate`);
export const duplicateFreelancerProfile = (id) => api.post(`/profile/freelancer/${id}/duplicate`);
export const deleteFreelancerProfile = (id) => api.delete(`/profile/freelancer/${id}`);
export const parseCV = (formData) => api.post("/profile/freelancer/parse-cv", formData, {
  headers: { "Content-Type": "multipart/form-data" },
  timeout: 120000, // 2 min for AI processing
});

// Paddle Payment endpoints
export const getPaddleConfig = () => api.get("/subscription/paddle-config");
export const cancelSubscription = () => api.post("/subscription/cancel");
